import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../components/ui/VehicleServiceHero';
import { CheckCircle, AlertTriangle, Monitor, Cpu, Zap, Settings, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedInventory from '../../../components/RelatedInventory';

const CheckEngineLight = () => {
  const diagnosticFeatures = [
    'Advanced OBD-II Scanning',
    'European Specialist Tools',
    'Real-Time Data Analysis',
    'Module Programming',
    'Same-Day Diagnosis',
    'Detailed Reports'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const commonCodes = [
    {
      code: 'P0300-P0308',
      meaning: 'Engine Misfire Detected',
      causes: ['Faulty spark plugs/coils', 'Fuel injector issues', 'Vacuum leaks', 'Carbon buildup'],
      severity: 'high'
    },
    {
      code: 'P0171/P0174',
      meaning: 'System Too Lean',
      causes: ['MAF sensor contamination', 'Vacuum leaks', 'Fuel pump weakness', 'O2 sensor failure'],
      severity: 'medium'
    },
    {
      code: 'P0420/P0430',
      meaning: 'Catalytic Converter Efficiency',
      causes: ['Catalytic converter failure', 'O2 sensor issues', 'Exhaust leaks', 'Engine misfires'],
      severity: 'high'
    },
    {
      code: 'P0128',
      meaning: 'Coolant Temperature Below Threshold',
      causes: ['Stuck-open thermostat', 'Low coolant level', 'Faulty coolant sensor', 'Radiator fan issues'],
      severity: 'medium'
    },
    {
      code: 'P0442',
      meaning: 'EVAP System Small Leak',
      causes: ['Loose gas cap', 'EVAP line cracks', 'Purge valve failure', 'Charcoal canister issues'],
      severity: 'low'
    },
    {
      code: 'P0507',
      meaning: 'Idle Speed Too High',
      causes: ['Vacuum leaks', 'Throttle body issues', 'IAC valve failure', 'PCM programming'],
      severity: 'medium'
    }
  ];

  const diagnosticServices = [
    {
      title: 'Computer Diagnostic Scanning',
      icon: Monitor,
      items: [
        'OBD-II code reading and analysis',
        'Live data stream monitoring',
        'Freeze frame data capture',
        'Pending code detection',
        'Module communication testing'
      ]
    },
    {
      title: 'European-Specific Diagnostics',
      icon: Cpu,
      items: [
        'BMW ISTA/INPA diagnostics',
        'Mercedes STAR/Xentry systems',
        'Audi/VW VCDS scanning',
        'Porsche PIWIS analysis',
        'Manufacturer-specific coding'
      ]
    },
    {
      title: 'Emission System Testing',
      icon: Activity,
      items: [
        'Catalytic converter efficiency',
        'O2 sensor response testing',
        'EVAP system leak detection',
        'EGR system functionality',
        'Smog pre-inspection'
      ]
    },
    {
      title: 'Electrical System Analysis',
      icon: Zap,
      items: [
        'Battery and charging tests',
        'Alternator output verification',
        'Starter draw testing',
        'Wiring harness inspection',
        'Ground point testing'
      ]
    }
  ];

  const warningLights = [
    { name: 'Check Engine', color: 'amber', desc: 'Engine or emissions issue' },
    { name: 'ABS', color: 'amber', desc: 'Anti-lock brake system fault' },
    { name: 'Oil Pressure', color: 'red', desc: 'Critical - stop driving immediately' },
    { name: 'Battery/Charging', color: 'red', desc: 'Charging system malfunction' },
    { name: 'Temperature', color: 'red', desc: 'Engine overheating warning' },
    { name: 'Tire Pressure (TPMS)', color: 'amber', desc: 'Low tire pressure detected' },
    { name: 'Brake System', color: 'red', desc: 'Brake fluid or system issue' },
    { name: 'Airbag (SRS)', color: 'red', desc: 'Safety system malfunction' }
  ];

  const diagnosticProcess = [
    { step: 1, title: 'Initial Code Scan', desc: 'Retrieve all stored and pending diagnostic trouble codes' },
    { step: 2, title: 'Data Analysis', desc: 'Review live data streams and freeze frame information' },
    { step: 3, title: 'Component Testing', desc: 'Test specific sensors and actuators for proper operation' },
    { step: 4, title: 'Visual Inspection', desc: 'Physical examination of related components and connections' },
    { step: 5, title: 'Root Cause Analysis', desc: 'Determine the actual problem, not just symptoms' },
    { step: 6, title: 'Repair Verification', desc: 'Clear codes and test drive to confirm repair success' }
  ];

  return (
    <>
      <Helmet>
        <title>Check Engine Light Repair Dallas | Vehicle Diagnostics | Paradise-Performance</title>
        <meta name="description" content="Expert check engine light diagnosis & repair in Dallas. Advanced computer diagnostics for European vehicles. BMW, Mercedes, Audi specialists. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/diagnostic-check-engine-light" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Check Engine Light Dallas",
            "description": "Expert vehicle diagnostics and check engine light repair",
            "url": "https://paradise-performance.com/services/diagnostic-check-engine-light",
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Plano", "Irving"],
            "priceRange": "$$"
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Vehicle Diagnostics"
        brandColor="#FF8C00"
        tagline="Check Engine Light & Computer Repair"
        description="Dallas's premier diagnostic center for European vehicles. Advanced computer analysis, emission testing, and electrical system troubleshooting. Same-day diagnosis available."
        features={diagnosticFeatures}
        heroImage="/assets/assets/images/new/engine-light/check-engine-light-on-1-copy.webp"
        heroImageAlt="Advanced vehicle diagnostic equipment"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Intro Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Dallas Vehicle Diagnostics & Check Engine Light Experts</h2>
            <p className="text-lg text-gray-700 mb-4">
              Modern European vehicles contain dozens of computer modules controlling everything from engine performance 
              to safety systems. When warning lights illuminate, you need technicians with the expertise and equipment 
              to diagnose complex electronic systems accurately.
            </p>
            <p className="text-lg text-gray-700">
              At Paradise-Performance, we utilize manufacturer-specific diagnostic tools and software that communicate 
              directly with your vehicle's onboard computers, providing insights that generic scanners simply cannot access.
            </p>
          </motion.div>

          {/* Warning Section */}
          <motion.div {...fadeInUp} className="bg-orange-50 border-2 border-orange-400 rounded-lg p-8 mb-16">
            <h2 className="text-3xl font-bold text-orange-900 mb-4 flex items-center">
              <AlertTriangle className="w-8 h-8 mr-3" />
              Don't Ignore Your Check Engine Light
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              While your vehicle may seem to run normally with the check engine light on, ignoring it can lead to:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Catalytic converter damage ($1,500-$4,000)',
                'Failed emissions testing',
                'Reduced fuel economy (up to 40%)',
                'Engine damage from misfires',
                'Transmission problems from sensor issues',
                'Voided warranty claims',
                'Safety system malfunctions',
                'Complete engine failure'
              ].map((issue, index) => (
                <div key={index} className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{issue}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <a href="tel:8173822316" className="inline-block px-8 py-3 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition-colors">
                Get Diagnosed Today
              </a>
            </div>
          </motion.div>

          {/* Common Codes Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Common Check Engine Light Codes We Diagnose</h2>
            <div className="space-y-4">
              {commonCodes.map((item, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-lg shadow-md p-6 border-l-4"
                  style={{
                    borderLeftColor: item.severity === 'high' ? '#DC2626' : 
                                    item.severity === 'medium' ? '#F59E0B' : '#10B981'
                  }}
                >
                  <div className="flex flex-wrap items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{item.code}</h3>
                      <p className="text-lg text-gray-700">{item.meaning}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      item.severity === 'high' ? 'bg-red-100 text-red-800' :
                      item.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {item.severity.toUpperCase()} PRIORITY
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Common Causes:</span>
                    <ul className="mt-2 grid md:grid-cols-2 gap-2">
                      {item.causes.map((cause, i) => (
                        <li key={i} className="flex items-center text-gray-600">
                          <CheckCircle className="w-4 h-4 text-gray-400 mr-2" />
                          {cause}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image Gallery */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/engine-light/enginelite01.avif"
                  alt="Computer diagnostic scanning"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold">Advanced OBD-II Scanning</p>
                  <p className="text-white/80 text-sm">Real-time data stream analysis</p>
                </div>
              </div>
              <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/engine-light/enginelite02.avif"
                  alt="European vehicle diagnostics"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold">European Specialist Tools</p>
                  <p className="text-white/80 text-sm">BMW ISTA, Mercedes STAR, VAG-COM</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Services Grid */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Comprehensive Diagnostic Services</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {diagnosticServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    {...fadeInUp}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-orange-500"
                  >
                    <div className="flex items-center mb-4">
                      <Icon className="w-8 h-8 text-orange-500 mr-3" />
                      <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Warning Lights Guide */}
          <motion.div {...fadeInUp} className="bg-white rounded-xl p-8 shadow-lg mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Dashboard Warning Lights Guide</h2>
            <p className="text-gray-700 mb-6">
              Understanding what each warning light means can help you determine the urgency of the situation:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {warningLights.map((light, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <div className={`w-4 h-4 rounded-full mr-3 ${
                      light.color === 'red' ? 'bg-red-500' : 'bg-amber-500'
                    }`} />
                    <h4 className="font-bold text-gray-900">{light.name}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{light.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 font-semibold">
                ⚠️ RED lights indicate serious issues requiring immediate attention. Stop driving and seek service immediately.
              </p>
            </div>
          </motion.div>

          {/* Process Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Diagnostic Process</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {diagnosticProcess.map((item, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.05 }}
                  className="relative bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="absolute -top-3 -left-3 w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 ml-4">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Technology Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl p-8 mb-16">
            <h2 className="text-3xl font-bold mb-6">Advanced Diagnostic Technology</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">Factory-Level Equipment</h3>
                <ul className="space-y-2">
                  {[
                    'BMW ISTA/ISTA-P diagnostic systems',
                    'Mercedes STAR/Xentry diagnostics',
                    'VAG-COM/VCDS for Audi/VW',
                    'Porsche PIWIS tester',
                    'Snap-on ZEUS diagnostic platform',
                    'Autel MaxiSYS Ultra scanner'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center">
                      <Settings className="w-5 h-5 text-orange-400 mr-3" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">Capabilities</h3>
                <ul className="space-y-2">
                  {[
                    'Module programming and coding',
                    'Key programming and immobilizer',
                    'Adaptation and calibration',
                    'Service interval reset',
                    'Component activation testing',
                    'Technical service bulletins access'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center">
                      <Cpu className="w-5 h-5 text-orange-400 mr-3" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Cost Section */}
          <motion.div {...fadeInUp} className="bg-gray-100 rounded-xl p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Diagnostic Service Pricing</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Basic Scan</h3>
                <p className="text-3xl font-bold text-orange-600 mb-3">$89</p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Code reading</li>
                  <li>• Basic interpretation</li>
                  <li>• Printed report</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border-2 border-orange-500">
                <div className="bg-orange-500 text-white text-sm font-bold px-2 py-1 rounded inline-block mb-2">RECOMMENDED</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Full Diagnostic</h3>
                <p className="text-3xl font-bold text-orange-600 mb-3">$149</p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Complete system scan</li>
                  <li>• Live data analysis</li>
                  <li>• Component testing</li>
                  <li>• Detailed report</li>
                  <li>• Repair recommendations</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">European Specialist</h3>
                <p className="text-3xl font-bold text-orange-600 mb-3">$189</p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Factory diagnostics</li>
                  <li>• Module coding</li>
                  <li>• Adaptation procedures</li>
                  <li>• TSB research</li>
                </ul>
              </div>
            </div>
            <p className="text-center mt-6 text-gray-600">
              * Diagnostic fees are waived when you proceed with recommended repairs
            </p>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Can I drive with the check engine light on?</h3>
                <p className="text-gray-700">
                  If the light is steady, you can usually drive to a service center. However, if it's flashing, 
                  this indicates active misfires that can damage your catalytic converter - stop driving immediately. 
                  Red warning lights always require immediate attention.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Why do European cars need special diagnostic tools?</h3>
                <p className="text-gray-700">
                  European manufacturers use proprietary communication protocols and coding that generic OBD-II 
                  scanners cannot access. Factory-level tools are required to properly diagnose, program, and 
                  code modules in BMW, Mercedes, Audi, and other European vehicles.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Will disconnecting the battery clear the codes?</h3>
                <p className="text-gray-700">
                  While disconnecting the battery may temporarily turn off the light, the underlying problem remains. 
                  The light will return once the system completes its self-tests. Additionally, this erases valuable 
                  diagnostic data and may affect other vehicle systems requiring reprogramming.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div {...fadeInUp} className="text-center bg-orange-500 text-white rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-4">Get Your Check Engine Light Diagnosed Today</h2>
            <p className="text-xl mb-8">
              Don't let a simple issue become an expensive repair. Our expert diagnostics identify 
              the real problem quickly and accurately.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8173822316" className="inline-block px-8 py-4 bg-white text-orange-600 font-bold rounded-lg hover:bg-gray-100 transition-colors">
                Call (817) 382-2316
              </a>
              <Link to="/contact" className="inline-block px-8 py-4 bg-orange-700 text-white font-bold rounded-lg hover:bg-orange-800 transition-colors">
                Schedule Diagnostic
              </Link>
            </div>
            <p className="mt-8 text-orange-100">
              Same-day appointments available • All makes and models • European specialists
            </p>
          </motion.div>
        </div>
      {/* Vehicle Inventory Links */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory limit={4} title="Vehicles For Sale" />
      </div>
      </div>

    </>
  );
};

export default CheckEngineLight;
